import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Shield,
    Database,
    EnvelopeSimple,
    GlobeHemisphereWest,
    ChartLineUp,
    Radio,
    LockKey,
    UserCircle,
    Cloud,
} from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';

const Section = ({ icon: Icon, title, children }) => (
    <section className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-7 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
            {Icon && <Icon size={26} weight="duotone" className="text-cyan-400" />}
            <h2
                className="text-2xl tracking-tight font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
            >
                {title}
            </h2>
        </div>

        <div className="space-y-4 text-gray-300 leading-relaxed">{children}</div>
    </section>
);

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    const processedDataItems = [
        t.privacyDataEmail,
        t.privacyDataUsername,
        t.privacyDataPasswordHash,
        t.privacyDataProfile,
        t.privacyDataAvatar,
        t.privacyDataLanguage,
        t.privacyDataFavorites,
        t.privacyDataComments,
        t.privacyDataFriends,
        t.privacyDataNotifications,
        t.privacyDataSession,
        t.privacyDataAccess,
    ];

    return (
        <div className="min-h-screen bg-[#050505] text-white">
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.14),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_35%)] pointer-events-none" />

                <header className="relative z-10 border-b border-white/5">
                    <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between gap-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-gray-300 hover:text-cyan-300 hover:border-cyan-400/40 transition-all"
                        >
                            <ArrowLeft size={18} />
                            {t.legalBack}
                        </button>

                        <div className="flex items-center gap-3 min-w-0">
                            <Radio
                                size={30}
                                weight="duotone"
                                className="text-cyan-400 flex-shrink-0"
                            />
                            <span
                                className="text-lg sm:text-xl font-black tracking-tighter truncate"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                                WORLDofRADIO
                            </span>
                        </div>
                    </div>
                </header>

                <main className="relative z-10 max-w-5xl mx-auto px-6 py-12 sm:py-16">
                    <div className="mb-10">
                        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300 mb-5">
                            <Shield size={18} weight="duotone" />
                            {t.privacyBadge}
                        </div>

                        <h1
                            className="text-4xl sm:text-5xl tracking-tighter font-black text-white mb-4"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                            {t.privacyTitle}
                        </h1>

                        <p className="text-gray-300 max-w-3xl leading-relaxed">
                            {t.privacyIntro}
                        </p>
                    </div>

                    <div className="space-y-6">
                        <Section icon={UserCircle} title={t.privacyControllerTitle}>
                            <div>
                                <p className="text-white font-semibold">Martin Wolf</p>
                                <p>Einzelunternehmer</p>
                                <p>Pegauer Straße 13</p>
                                <p>04613 Lucka</p>
                                <p>Deutschland</p>
                            </div>

                            <div>
                                <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                    {t.legalContact}
                                </p>
                                <a
                                    href="mailto:info@worldofradio.de"
                                    className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold transition-colors"
                                >
                                    <EnvelopeSimple size={20} />
                                    info@worldofradio.de
                                </a>
                            </div>
                        </Section>

                        <Section icon={Database} title={t.privacyProcessedDataTitle}>
                            <p>{t.privacyProcessedDataIntro}</p>

                            <ul className="list-disc list-inside space-y-2 text-gray-300">
                                {processedDataItems.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </Section>

                        <Section icon={LockKey} title={t.privacyPurposeTitle}>
                            <p>{t.privacyPurposeText}</p>
                        </Section>

                        <Section icon={Cloud} title={t.privacyHostingTitle}>
                            <p>{t.privacyHostingText1}</p>
                            <p>{t.privacyHostingText2}</p>
                        </Section>

                        <Section icon={ChartLineUp} title={t.privacyAnalyticsTitle}>
                            <p>{t.privacyAnalyticsText}</p>
                        </Section>

                        <Section icon={EnvelopeSimple} title={t.privacyMailTitle}>
                            <p>{t.privacyMailText}</p>
                        </Section>

                        <Section icon={GlobeHemisphereWest} title={t.privacyFontsTitle}>
                            <p>{t.privacyFontsText}</p>
                        </Section>

                        <Section icon={Radio} title={t.privacyRadioTitle}>
                            <p>{t.privacyRadioText1}</p>
                            <p>{t.privacyRadioText2}</p>
                        </Section>

                        <Section icon={Shield} title={t.privacyStorageTitle}>
                            <p>{t.privacyStorageText}</p>
                        </Section>

                        <Section icon={Database} title={t.privacyRetentionTitle}>
                            <p>{t.privacyRetentionText}</p>
                        </Section>

                        <Section icon={Shield} title={t.privacyRightsTitle}>
                            <p>{t.privacyRightsText1}</p>
                            <p>{t.privacyRightsText2}</p>

                            <a
                                href="mailto:info@worldofradio.de"
                                className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold transition-colors"
                            >
                                <EnvelopeSimple size={20} />
                                info@worldofradio.de
                            </a>
                        </Section>

                        <section className="rounded-3xl border border-cyan-400/20 bg-cyan-400/10 p-7 sm:p-8">
                            <p className="text-sm text-cyan-100 leading-relaxed">
                                {t.privacyUpdated}
                            </p>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default PrivacyPolicy;